import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import AOS from 'aos'
import Text from '../../../../../elements/Typo/Text'
import { useKpiApi } from '../../../../../store/api/hooks/KpiApiHook'
import { ErrorResponse, SuccessResponse } from '../../../../../store/api/apiTypes'
import toast from 'react-hot-toast'
import { Data } from 'plotly.js'
import { getChartColor } from '../../../../../_config'
import DataTableLoader from '../../../../../components/DataTableLoader'
import { BarChart, Bar, Rectangle, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { convertToKFormat, getDecimalPlacesByRange } from '../../../../../_common/functions'

export const CustomTooltip = ({ active, payload, label, decimalDigit }: any) => {
    if (active && payload?.length) {
        const { name, ...payloadData } = payload[0].payload
        return (
            <div className="bg-white shadow-lg rounded-[0.667rem] p-2">
                <Text className="text-[0.417rem] text-center">{name}</Text>
                <div className="bg-[#EFF2F799] px-2 py-1 rounded-[0.667rem]">
                    {Object.keys(payloadData).map((param: any, i: any) => (
                        <div className="flex items-center gap-2" key={`tooltip-${param + i}`}>
                            <div
                                className="w-[0.583rem] h-[0.583rem] rounded-[0.167rem]"
                                style={{
                                    background: getChartColor(param, i),
                                }}
                            ></div>
                            <Text size="xs" className="flex-grow text-[#74788D] mr-4">
                                {param}
                            </Text>
                            <Text size="xl" className="font-bold">
                                {payloadData[param] && Number.isNaN(+payloadData[param]) ? payloadData[param] : convertToKFormat(payloadData[param], decimalDigit)}
                            </Text>
                        </div>
                    ))}
                </div>
            </div>
        )
    }
    return null
}

interface AvgMetricAltI {
    [key: string]: {
        [key: string]: string
    }[]
}
interface AvgMetricI {
    [key: string]: {
        [key: string]: {
            [key: string]: string
        }
    }
}
interface Props {
    experimentDetails: any
    selectedGroup: string | null
    selectedKPI: string | null
    selectedDateRange: any[]
    selectedOptionConfig: string | null | undefined
    toDownloadAsReport?: boolean
    reportError?: (x: boolean) => void
}

const legendContent = ({ payload }: any) => {
    return (
        <div className="flex justify-center items-center gap-3">
            {payload?.map((eachPayload: any, index: number) => (
                <div className="flex items-center" key={eachPayload.color + index}>
                    <div
                        className={`w-[0.333rem] h-[0.333rem] rounded-full bg-[${eachPayload.color}]`}
                        style={{
                            backgroundColor: eachPayload.color,
                        }}
                    />
                    <Text size="xs" className="ml-1">
                        {eachPayload.value}
                    </Text>
                </div>
            ))}
        </div>
    )
}
const AverageMetric: React.FC<Props> = ({ experimentDetails, selectedGroup, selectedKPI, selectedDateRange, selectedOptionConfig, toDownloadAsReport = false, reportError }) => {
    const { t } = useTranslation()
    const { getAvgMetric } = useKpiApi()
    const [responseData, setResponseData] = useState<{ [key: string]: Data[] }>({})
    const [avgMetricLoading, setAvgMetricLoading] = useState<boolean>(false)
    const [decimalDigit, setDecimalDigit] = useState<number>(2)

    const _handleFetchAvgMetric = (group_name: string) => {
        setAvgMetricLoading(true)
        const params: any = {
            literal: experimentDetails.experiment_literal,
            group_name,
            kpiname: selectedKPI,
        }

        if (selectedOptionConfig != undefined) {
            params.methodology = selectedOptionConfig
        }

        if (selectedDateRange.length == 2) {
            params.measurement_start_date = selectedDateRange[0]
            params.measurement_end_date = selectedDateRange[1]
            params.is_slice_and_dice = true
        } else {
            params.measurement_start_date = null
            params.measurement_end_date = null
            params.is_slice_and_dice = false
        }
        getAvgMetric(
            params,
            (resp: SuccessResponse<AvgMetricAltI>) => {
                if (selectedKPI == 'Total Distribution') {
                    const _data: any =
                        resp.data?.['Total Distribution']
                            ?.filter((item: any) => item.name === 'After')
                            .map((item: any) => {
                                return {
                                    Counterfactual: item['Synthetic Control'],
                                    Test: item['Test'],
                                    name: 'Measurement/Analysis Period',
                                }
                            }) || []
                    setResponseData({ 'Total Distribution': _data })
                } else {
                    setResponseData(resp.data)
                }

                setAvgMetricLoading(false)
            },
            (error: ErrorResponse<AvgMetricI>) => {
                setAvgMetricLoading(false)
                if (reportError) {
                    reportError(true)
                }
                toast.error(error?.message || t('Something went wrong'))
            },
        )
    }

    useEffect(() => {
        if (experimentDetails?.experiment_literal && selectedGroup) {
            _handleFetchAvgMetric(selectedGroup)
        }
    }, [experimentDetails, selectedGroup, selectedOptionConfig, selectedKPI, selectedDateRange])

    const getDefaultBarWidth = (rem: number) => {
        const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize)
        return rem * rootFontSize
    }

    useEffect(() => {
        const params: any = {}

        if (toDownloadAsReport) {
            params['disable'] = true // Animation disabled
        } else {
            params['once'] = false // Animation occurs every time you scroll to the element
        }

        AOS.init(params)
        AOS.refresh()
    }, [])

    useEffect(() => {
        if (selectedKPI && responseData?.[selectedKPI]?.length) {
            const keys: (keyof Data)[] = Object.keys(responseData[selectedKPI][0]) as (keyof Data)[]
            const values: any[] = []
            for (const gr of keys.filter((k) => k !== 'name')) {
                values.push(responseData?.[selectedKPI]?.[0]?.[gr])
            }

            setDecimalDigit(getDecimalPlacesByRange(values))
        }
    }, [selectedKPI, responseData])

    return (
        <div className="col-span-3 md:col-span-1 bg-base-100 rounded-[0.45rem] shadow-1" data-aos="zoom-in" data-aos-delay="200">
            <div className="px-3 py-3 roushared with:nded-[0.7rem]">
                <div className="flex justify-between items-stretch mb-0 gap-1">
                    <div className="flex flex-col">
                        <Text size="md" className="font-semibold">
                            {t('Average Metric')}
                        </Text>
                        <Text size="md" className="!text-[0.5rem]">
                            {t(`(${selectedKPI == 'Total Distribution' ? 'Measurement/Analysis Period' : 'Before and After'})`)}
                        </Text>
                    </div>
                </div>

                {avgMetricLoading ? (
                    <div className="mt-5">
                        <DataTableLoader className="h-4 w-4" />
                    </div>
                ) : (
                    <div className="mt-2">
                        <div className="w-full h-[14.542rem] text-[0.5rem] flex relative">
                            <ResponsiveContainer width="100%" height="100%" className="flex-grow">
                                <BarChart
                                    data={selectedKPI ? responseData?.[selectedKPI] || [] : []}
                                    margin={{
                                        top: 20,
                                        right: 0,
                                        left: 10,
                                        bottom: 0,
                                    }}
                                    barGap={20}
                                >
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={false} />
                                    <XAxis dataKey="name" stroke="#939393" tickLine={false} />
                                    <YAxis
                                        label={{
                                            value: t('Avg. {{kpi}}', { kpi: (selectedKPI ?? '').split('_').join('') }), // Label text
                                            angle: -90, // Rotate the label to vertical
                                            position: 'insideLeft', // Position of the label
                                            offset: 0,
                                            color: '#808080',
                                        }}
                                        width={60}
                                        axisLine={false}
                                        tickFormatter={(value: any) => convertToKFormat(value, decimalDigit)}
                                    />
                                    <Tooltip content={<CustomTooltip decimalDigit={decimalDigit} />} cursor={{ fill: 'transparent' }} />
                                    <Legend iconType="circle" content={legendContent} />
                                    {!!selectedKPI &&
                                        !!responseData?.[selectedKPI]?.length &&
                                        Object.keys(responseData?.[selectedKPI]?.[0] ?? {})
                                            .filter((e: string) => e !== 'name')
                                            .map((gr: any, i: number) => {
                                                return (
                                                    <Bar
                                                        key={gr + i}
                                                        dataKey={gr}
                                                        fill={getChartColor(gr, i)}
                                                        radius={[7, 7, 7, 7]}
                                                        barSize={getDefaultBarWidth(1.25)}
                                                        label={{
                                                            position: 'top',
                                                            textAnchor: 'middle',
                                                            formatter: (value: any) => {
                                                                if (value == null || value === '') return ''

                                                                const numericValue = +value
                                                                if (Number.isNaN(numericValue)) return String(value)

                                                                return convertToKFormat(numericValue, decimalDigit)
                                                            },
                                                        }}
                                                        activeBar={<Rectangle fill={getChartColor(gr, i)} />}
                                                        isAnimationActive={!toDownloadAsReport}
                                                    />
                                                )
                                            })}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
export default AverageMetric
